/**
 * Environment Configuration for AI Services
 * 
 * This file manages environment-specific configuration for AI processing,
 * API keys, and feature flags.
 * 
 * Environment Variables:
 * - REACT_APP_AI_PROVIDER: 'gemini' (default) or 'openai'
 * - REACT_APP_AI_ENABLED: Enable/disable AI processing (default: true)
 * - REACT_APP_AI_TIMEOUT: AI request timeout in milliseconds (default: 30000)
 * - REACT_APP_AI_MAX_RETRIES: Maximum retry attempts (default: 3)
 *
 * NOTE: REACT_APP_OPENAI_API_KEY was removed in the October 2025 security migration
 * and is not read anywhere in this package - AI provider keys live server-side in the
 * Cloud Functions. This header listed it as "required" until 2026-08-18.
 */

/**
 * Gets environment configuration with defaults
 */
export const AI_ENVIRONMENT = {
  // AI Provider Selection
  AI_PROVIDER: process.env.REACT_APP_AI_PROVIDER || 'gemini', // 'openai' or 'gemini' (default: gemini)

  // Feature Flags
  AI_ENABLED: process.env.REACT_APP_AI_ENABLED !== 'false', // Default to true unless explicitly disabled
  AI_CATEGORIZATION_ENABLED: process.env.REACT_APP_AI_CATEGORIZATION_ENABLED !== 'false',
  AI_TITLE_GENERATION_ENABLED: process.env.REACT_APP_AI_TITLE_GENERATION_ENABLED !== 'false',
  AI_SUMMARY_GENERATION_ENABLED: process.env.REACT_APP_AI_SUMMARY_GENERATION_ENABLED !== 'false',
  AI_DATA_EXTRACTION_ENABLED: process.env.REACT_APP_AI_DATA_EXTRACTION_ENABLED !== 'false',

  // Performance Configuration
  AI_TIMEOUT: parseInt(process.env.REACT_APP_AI_TIMEOUT) || 30000, // 30 seconds
  AI_MAX_RETRIES: parseInt(process.env.REACT_APP_AI_MAX_RETRIES) || 3,
  AI_RATE_LIMIT: parseInt(process.env.REACT_APP_AI_RATE_LIMIT) || 60, // requests per minute
  AI_BATCH_SIZE: parseInt(process.env.REACT_APP_AI_BATCH_SIZE) || 5,

  // Development/Debug Flags
  AI_DEBUG_MODE: process.env.NODE_ENV === 'development' || process.env.REACT_APP_AI_DEBUG === 'true',
  AI_MOCK_MODE: process.env.REACT_APP_AI_MOCK_MODE === 'true', // Use mock responses instead of real AI

  // Fallback Configuration
  FALLBACK_TO_BASIC_ON_AI_FAILURE: process.env.REACT_APP_FALLBACK_ON_AI_FAILURE !== 'false',
  SHOW_AI_STATUS_TO_USER: process.env.REACT_APP_SHOW_AI_STATUS === 'true',

  // Cost Management
  AI_COST_TRACKING_ENABLED: process.env.REACT_APP_AI_COST_TRACKING === 'true',
  AI_DAILY_REQUEST_LIMIT: parseInt(process.env.REACT_APP_AI_DAILY_LIMIT) || 1000
};

/**
 * Validates the environment configuration
 * @returns {Object} Validation result with warnings and errors
 */
export const validateEnvironment = () => {
  const warnings = [];
  const errors = [];

  // Read current environment values (not cached AI_ENVIRONMENT)
  // This allows tests to modify process.env and have validation reflect those changes
  const currentAiEnabled = process.env.REACT_APP_AI_ENABLED !== 'false';
  const currentTimeout = parseInt(process.env.REACT_APP_AI_TIMEOUT) || 30000;
  const currentRateLimit = parseInt(process.env.REACT_APP_AI_RATE_LIMIT) || 60;
  const currentMockMode = process.env.REACT_APP_AI_MOCK_MODE === 'true';

  // Note: OpenAI API keys are managed server-side by Cloud Functions only.

  // Check timeout values
  if (currentTimeout < 5000) {
    warnings.push('AI timeout is very low (< 5 seconds), may cause frequent timeouts');
  }

  if (currentTimeout > 60000) {
    warnings.push('AI timeout is very high (> 60 seconds), may cause poor user experience');
  }

  // Check rate limits
  if (currentRateLimit > 100) {
    warnings.push('AI rate limit is high, may cause API quota issues');
  }

  // Check mock mode in production
  if (process.env.NODE_ENV === 'production' && currentMockMode) {
    errors.push('AI mock mode is enabled in production environment');
  }

  return {
    isValid: errors.length === 0,
    warnings,
    errors,
    config: AI_ENVIRONMENT
  };
};

/**
 * Gets a safe environment summary (without sensitive data)
 * @returns {Object} Safe environment configuration for logging
 */
export const getSafeEnvironmentSummary = () => {
  // Read current environment values (not cached AI_ENVIRONMENT)
  // This allows tests to modify process.env and have summary reflect those changes
  const currentAiEnabled = process.env.REACT_APP_AI_ENABLED !== 'false';
  const currentTimeout = parseInt(process.env.REACT_APP_AI_TIMEOUT) || 30000;
  const currentMaxRetries = parseInt(process.env.REACT_APP_AI_MAX_RETRIES) || 3;
  const currentRateLimit = parseInt(process.env.REACT_APP_AI_RATE_LIMIT) || 60;
  const currentDebugMode = process.env.NODE_ENV === 'development' || process.env.REACT_APP_AI_DEBUG === 'true';
  const currentMockMode = process.env.REACT_APP_AI_MOCK_MODE === 'true';
  const currentFallbackEnabled = process.env.REACT_APP_FALLBACK_ON_AI_FAILURE !== 'false';

  // Must match AI_ENVIRONMENT.AI_PROVIDER above — this default used to be 'openai',
  // which made the startup log claim OpenAI on a build that actually dispatches to Gemini.
  const currentProvider = process.env.REACT_APP_AI_PROVIDER || 'gemini';

  return {
    aiProvider: currentProvider,
    aiEnabled: currentAiEnabled,
    timeout: currentTimeout,
    maxRetries: currentMaxRetries,
    rateLimit: currentRateLimit,
    debugMode: currentDebugMode,
    mockMode: currentMockMode,
    fallbackEnabled: currentFallbackEnabled,
    environment: process.env.NODE_ENV
  };
};

/**
 * Logs environment status on startup
 */
export const logEnvironmentStatus = () => {
  const validation = validateEnvironment();
  const summary = getSafeEnvironmentSummary();

  console.log('🤖 AI Service Environment Configuration:', summary);


  if (validation.warnings.length > 0) {
    console.warn('⚠️ Environment warnings:', validation.warnings);
  }

  if (validation.errors.length > 0) {
    console.error('❌ Environment errors:', validation.errors);
    // Log each error individually for clarity
    validation.errors.forEach((error, index) => {
      console.error(`❌ Error ${index + 1}:`, error);
    });
  }

  if (validation.isValid) {
    console.log('✅ AI environment configuration is valid');
  } else {
    console.error('❌ AI environment configuration has errors');
  }
};

export default AI_ENVIRONMENT;